import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Badge, Button, LoaderPage } from '@freelancelabs/components';
import { ArrowLeft } from '@freelancelabs/icons';

import Card from '../../components/Card';
import { ListSkeleton, MatrixSkeleton } from '../../components/Skeletons';
import { RoleScope } from '../../graphql/__generated__/schema';
import {
    AccountUsersDocument,
    PermissionsDocument,
    PortalModulesDocument,
    RoleSummaryFragment,
    RolesDocument,
} from '../../graphql/__generated__/types';
import { accountsCell } from '../users/aggregate';
import { EMPTY, formatDate } from '../users/display';
import { statusCategory, statusLabel } from '../users/status';
import RightsMatrix from './RightsMatrix';

const HOLDERS_SHOWN = 25;

const RoleDetailPage = () => {
    const { roleId } = useParams<{ roleId: string }>();
    const { data, loading, error } = useQuery(RolesDocument);
    // The matrix reads rights and modules together: on one alone, every module right would
    // file under "unknown module" while the other is in flight.
    const {
        data: referentialData,
        loading: referentialLoading,
        error: referentialError,
    } = useQuery(PermissionsDocument);
    const { data: modulesData, loading: modulesLoading, error: modulesError } = useQuery(PortalModulesDocument);
    const { data: holdersData, loading: holdersLoading } = useQuery(AccountUsersDocument, {
        variables: { roleId, limit: HOLDERS_SHOWN, offset: 0 },
        skip: !roleId,
    });

    const backToRoles = (
        <Link to="/roles" className="link inline-flex items-center gap-1.5 text-sm">
            <ArrowLeft size={14} />
            {c('Roles').t`Roles`}
        </Link>
    );

    if (loading) {
        return <LoaderPage />;
    }

    const roles: RoleSummaryFragment[] = [...(data?.portal ?? []), ...(data?.backoffice ?? [])];
    const role = roles.find((candidate) => candidate.id === roleId);

    if (error || !role) {
        return (
            <section className="flex flex-col gap-3">
                {backToRoles}
                <h1 className="text-xl font-semibold text-text-900">{c('Roles').t`Role not found`}</h1>
                <p className="text-text-600">
                    {c('Roles').t`This role is not in the referential, or it could not be loaded.`}
                </p>
            </section>
        );
    }

    const granted = new Set((role.permissions ?? []).map((permission) => permission?.code ?? ''));
    const rightsCount = role.permissions?.length ?? 0;
    const userCount = role.membershipCount ?? 0;
    const referential = (referentialData?.permissions ?? []).filter(Boolean);
    const modules = modulesData?.portalModules ?? [];
    const matrixLoading = referentialLoading || modulesLoading;
    const matrixError = referentialError ?? modulesError;

    const holders = (holdersData?.accountUsers?.items ?? []).filter(Boolean);
    const holdersTotal = holdersData?.accountUsers?.total ?? 0;

    const deleteReason =
        userCount > 0
            ? c('Roles').t`A role can only be deleted once nobody holds it. Reassign the users first.`
            : c('Roles').t`Deleting a role is not open yet: a role is shared by every client account.`;

    const editReason = c('Roles')
        .t`Editing is not open yet: a role is shared by every client account, so a change here would apply to all of them at once.`;

    return (
        <section className="flex flex-col gap-5">
            {backToRoles}

            <header className="flex flex-wrap items-start gap-4">
                <div>
                    <div className="flex flex-wrap items-baseline gap-3">
                        <h1 className="text-xl font-semibold text-text-900">{role.name}</h1>
                        <Badge size="small" category={role.scope === RoleScope.Backoffice ? 'info' : 'default'}>
                            {role.scope === RoleScope.Backoffice
                                ? c('Role scope').t`Back-office`
                                : c('Role scope').t`Portal`}
                        </Badge>
                    </div>
                    <p className="mt-1 text-xs text-text-600">
                        {c('Roles').ngettext(
                            msgid`${rightsCount} right granted`,
                            `${rightsCount} rights granted`,
                            rightsCount
                        )}
                        {' · '}
                        {userCount === 0
                            ? c('Roles').t`no user holds it`
                            : ngettext(msgid`${userCount} user holds it`, `${userCount} users hold it`, userCount)}
                    </p>
                </div>
                <Button className="ml-auto" category="destructive" disabled title={deleteReason}>
                    {c('Roles').t`Delete the role`}
                </Button>
            </header>

            <p className="-mt-3 text-sm text-text-500">{deleteReason}</p>

            {role.description && (
                <p className="max-w-2xl border-l-2 border-admin-accent pl-3 text-text-700">{role.description}</p>
            )}

            <Card
                title={c('Roles').t`General information`}
                actions={
                    <Button category="weak" size="small" disabled title={editReason}>
                        {c('Action').t`Edit`}
                    </Button>
                }
            >
                <dl className="grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2">
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-text-500">{c('Roles').t`Role name`}</dt>
                        <dd className="mt-0.5 text-sm text-text-900">{role.name}</dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-text-500">{c('Roles').t`Code`}</dt>
                        <dd className="mt-0.5 font-mono text-xs text-text-900">{role.code}</dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-text-500">{c('Roles').t`Users`}</dt>
                        <dd className="mt-0.5 font-mono text-xs text-text-900">{userCount}</dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-text-500">
                            {c('Roles column').t`Created on`}
                        </dt>
                        <dd className="mt-0.5 font-mono text-xs text-text-900">{formatDate(role.createdAt)}</dd>
                    </div>
                    <div className="sm:col-span-2">
                        <dt className="text-xs uppercase tracking-wide text-text-500">{c('Roles').t`Reach`}</dt>
                        <dd className="mt-0.5 text-sm text-text-900">{c('Roles').t`All accounts`}</dd>
                    </div>
                </dl>
            </Card>

            <Card title={c('Roles').t`Users holding this role`} counter={holdersTotal}>
                {holdersLoading ? (
                    <ListSkeleton rows={5} />
                ) : holders.length === 0 ? (
                    <p className="text-xs text-text-600">
                        {c('Roles').t`No user holds this role on the accounts you can see.`}
                    </p>
                ) : (
                    <>
                        <div
                            className="fl-table-container"
                            tabIndex={0}
                            role="region"
                            aria-label={c('Roles').t`Users holding this role`}
                        >
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border-norm text-left text-xs uppercase tracking-wide text-text-500">
                                        <th className="py-2 pr-4 font-semibold">{c('Users column').t`Last name`}</th>
                                        <th className="py-2 pr-4 font-semibold">{c('Users column').t`First name`}</th>
                                        <th className="py-2 pr-4 font-semibold">{c('Users column').t`Email`}</th>
                                        <th className="py-2 pr-4 font-semibold">{c('Users column').t`Account`}</th>
                                        <th className="py-2 pr-4 font-semibold">{c('Users column').t`Status`}</th>
                                        <th className="py-2 font-semibold" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {holders.map((holder) => {
                                        const accounts = accountsCell(holder);
                                        return (
                                            <tr key={holder.userId} className="border-b border-border-weak">
                                                <td className="py-2.5 pr-4 text-text-900">
                                                    {holder.lastName ?? EMPTY}
                                                </td>
                                                <td className="py-2.5 pr-4">{holder.firstName ?? EMPTY}</td>
                                                <td className="py-2.5 pr-4 font-mono text-xs">{holder.email}</td>
                                                <td className="py-2.5 pr-4">
                                                    {accounts.first}
                                                    {accounts.surplus > 0 && (
                                                        <span
                                                            className="ml-1 cursor-help rounded border border-border-norm bg-background-weak px-1 text-xs text-text-600"
                                                            title={accounts.othersTitle}
                                                            tabIndex={0}
                                                        >
                                                            +{accounts.surplus}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-2.5 pr-4">
                                                    {holder.status && (
                                                        <Badge category={statusCategory(holder.status)} size="small">
                                                            {statusLabel(holder.status)}
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td className="py-2.5 text-end">
                                                    <Button
                                                        to={`/utilisateurs/${holder.userId}`}
                                                        category="weak"
                                                        size="small"
                                                    >
                                                        {c('Roles').t`See the sheet`}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {holdersTotal > holders.length && (
                            <p className="mt-3 text-sm">
                                <Link to={`/utilisateurs?role=${role.id}`} className="link">
                                    {c('Roles').t`See every user holding this role`}
                                </Link>
                            </p>
                        )}
                    </>
                )}
            </Card>

            <Card
                title={c('Roles').t`Rights`}
                actions={
                    <Button category="weak" size="small" disabled title={editReason}>
                        {c('Action').t`Edit`}
                    </Button>
                }
            >
                {matrixError ? (
                    <p className="text-sm text-danger-600" role="alert">
                        {c('Roles').t`The rights referential could not be loaded.`}
                    </p>
                ) : matrixLoading ? (
                    <MatrixSkeleton />
                ) : (
                    <RightsMatrix referential={referential} modules={modules} granted={granted} />
                )}
            </Card>
        </section>
    );
};

export default RoleDetailPage;
